/**
 * 沉浸模式管理 — pointer-lock 和移动端空闲时自动隐藏 HUD。
 *
 * 桌面端：pointer-lock 激活时进入沉浸，释放后退出。
 * 移动端：fly/walk/avatar 模式下触摸空闲后进入沉浸，明确 tap（短按非拖拽）退出。
 */
import { useEffect, useRef } from 'react';
import { useViewerStore } from '@stores/viewerStore';

/** 移动端触摸空闲后自动进入沉浸的延迟 */
const MOBILE_IDLE_MS = 3000;
/** tap 判定：触摸时长 < 此值 且 位移 < TAP_MOVE_PX 视为点击 */
const TAP_MAX_MS = 300;
const TAP_MOVE_PX = 10;

const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

export function useImmersiveMode() {
  const navMode = useViewerStore((s) => s.navigationMode);
  const setImmersive = useViewerStore((s) => s.setImmersive);
  // 惰性初始化，避免每次渲染都调用 isTouchDevice()
  const isMobileRef = useRef<boolean | null>(null);
  if (isMobileRef.current === null) {
    isMobileRef.current = isTouchDevice();
  }
  const isMobile = isMobileRef.current;

  // 桌面端：pointer-lock 状态驱动沉浸
  useEffect(() => {
    if (isMobile) return;
    const handler = () => setImmersive(!!document.pointerLockElement);
    document.addEventListener('pointerlockchange', handler);
    return () => document.removeEventListener('pointerlockchange', handler);
  }, [isMobile, setImmersive]);

  // 移动端：fly/walk/avatar 模式空闲检测 + tap 退出
  useEffect(() => {
    if (!isMobile) return;
    const isGaming = navMode === 'fly' || navMode === 'walk' || navMode === 'avatar';
    if (!isGaming) {
      setImmersive(false);
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    let touchStartTime = 0;
    let touchStartX = 0;
    let touchStartY = 0;

    const startTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setImmersive(true), MOBILE_IDLE_MS);
    };

    const onTouchStart = (e: TouchEvent) => {
      touchStartTime = Date.now();
      const t = e.touches[0];
      if (t) {
        touchStartX = t.clientX;
        touchStartY = t.clientY;
      }
      // 任何触摸都重启空闲计时（不论是否沉浸中）
      startTimer();
    };

    const onTouchEnd = (e: TouchEvent) => {
      // 只有明确的 tap（短按 + 小位移）才退出沉浸
      if (!useViewerStore.getState().immersive) return;
      const dt = Date.now() - touchStartTime;
      const t = e.changedTouches[0];
      const dist = t ? Math.hypot(t.clientX - touchStartX, t.clientY - touchStartY) : 0;
      if (dt < TAP_MAX_MS && dist < TAP_MOVE_PX) {
        setImmersive(false);
        startTimer();
      }
    };

    startTimer();
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
      setImmersive(false);
    };
  }, [isMobile, navMode, setImmersive]);

  // 退回 orbit 时确保退出沉浸
  useEffect(() => {
    if (navMode === 'orbit') setImmersive(false);
  }, [navMode, setImmersive]);
}
